import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import {
    CallSignOption,
    EditUnitForm,
    EditUnitFormType,
    EditUnitOptions,
    EditUnitResource,
} from '@mark43/cad-api';
import { FrPage } from '../fr-page-layout/fr-page';
import { FrPageBody } from '../fr-page-layout/fr-page-body';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FrPageFooter } from '../fr-page-layout/fr-page-footer';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { LogOnEditUnitForm } from '../log-on-edit-unit/log-on-edit-unit-form/log-on-edit-unit-form';
import { useCurrentUser } from '../current-user/current-user';
import { EDIT_UNIT_DEFAULT_FORM_VALUES } from '../log-on-edit-unit/constants';
import { useEditUnitFormMutation } from '../log-on-edit-unit/hooks/use-edit-unit-form-mutation';
import { useEditUnitFormState } from '../log-on-edit-unit/hooks/use-edit-unit-form-state';
import { isValidSelectedPersonnel } from '../log-on-edit-unit/helpers';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { ROOT_PATH } from '../router/constants';
import { useLocationBias } from '../location-bias/use-location-bias';

export const FrEditUnitPage = observer(function FrEditUnitPage() {
    const [currentCallSign, setCurrentCallSign] = useState<CallSignOption | null>(null);
    const [currentEditUnitOptions, setCurrentEditUnitOptions] = useState<
        EditUnitOptions | undefined
    >(undefined);

    const unitAssignment = useUnitAssignment();
    const currentUser = useCurrentUser();
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const { setLocationBiasId } = useLocationBias();

    const defaultPersonnel = useMemo(() => {
        const { firstName, lastName, userId, departmentAgencyId } = currentUser;

        return {
            label: `${firstName} ${lastName}`,
            value: userId,
            agencyId: departmentAgencyId,
        };
    }, [currentUser]);

    const defaultValues = {
        ...EDIT_UNIT_DEFAULT_FORM_VALUES,
        personnel: [defaultPersonnel],
    };

    const form = useForm<EditUnitFormType>({
        defaultValues,
        mode: 'onBlur',
    });

    const selectedCallSign: CallSignOption | null = useWatch<EditUnitFormType, 'callSign'>({
        name: 'callSign',
        control: form.control,
    });

    const isQueryEnabled =
        !currentCallSign ||
        !!(selectedCallSign && currentCallSign.value !== selectedCallSign.value);

    const existingUnitQuery = useQuery(
        ['edit-unit-bootstrap', selectedCallSign?.value],
        () => {
            return apiClient
                .request<EditUnitResource.Bootstrap>({
                    method: 'POST',
                    url: 'edit_unit/bootstrap',
                    data: {
                        unitId: selectedCallSign?.value,
                    },
                })
                .then((resp) => {
                    if (!currentEditUnitOptions) {
                        // check if it's the first load to set all the current options without any unit id filter
                        setCurrentEditUnitOptions(resp.data.data?.options);
                    }
                    return resp.data.data;
                });
        },
        {
            enabled: isQueryEnabled,
        }
    );

    const existingUnitData = existingUnitQuery.data;

    const updateCurrentCallSign = useCallback((callSign: CallSignOption) => {
        setCurrentCallSign(callSign);
    }, []);

    const {
        hasLoadedOnce,
        formFieldOptions,
        statics,
        setUserChangedStaffCount,
    } = useEditUnitFormState({
        form,
        existingUnitData,
        editUnitOptions: currentEditUnitOptions,
        updateCurrentCallSign,
        defaultPersonnel,
    });

    const mutation = useEditUnitFormMutation({
        onMutationSuccess: () => {
            if (unitAssignment?.unitId) {
                navigate(ROOT_PATH);
            } else {
                form.reset(defaultValues);
                existingUnitQuery.refetch();
            }
            setLocationBiasId?.(unitAssignment?.unitDispatchAreaId);
        },
    });

    const onSubmit = useCallback(
        (props: EditUnitFormType): void => {
            mutation.mutate({
                ...props,
                staffCount: Number(props.staffCount),
                unitId: unitAssignment?.unitId,
                personnel: props.personnel.filter(isValidSelectedPersonnel),
            } as EditUnitForm);
        },
        [mutation, unitAssignment?.unitId]
    );

    const query = useMemo(() => {
        return {
            isInitialLoading: hasLoadedOnce ? false : existingUnitQuery.isInitialLoading,
            isError: existingUnitQuery.isError,
        };
    }, [existingUnitQuery, hasLoadedOnce]);

    return (
        <FrPage>
            <FrPageHeader title="Edit Unit" showGoBackButton={!!unitAssignment?.unitId} />
            <IntegratedFormProvider
                query={query}
                mutation={mutation}
                form={form}
                onSubmit={onSubmit}
            >
                <FrPageBody
                    customStyles={{
                        alignSelf: 'baseline',
                        padding: '12px',
                        overflowY: 'auto',
                    }}
                >
                    <FormBody formBodyStyle={{ width: '50%' }}>
                        <LogOnEditUnitForm
                            form={form}
                            formFieldOptions={formFieldOptions}
                            statics={statics}
                            isEditUnit={!!unitAssignment?.unitId}
                            setUserChangedStaffCount={setUserChangedStaffCount}
                        />
                    </FormBody>
                </FrPageBody>
                <FrPageFooter>
                    <FormSaveButton size="xl" data-testid="save-edit-unit-fr-page" />
                    {unitAssignment?.unitId && (
                        <FormCancelButton onClick={() => navigate(ROOT_PATH)} />
                    )}
                </FrPageFooter>
            </IntegratedFormProvider>
        </FrPage>
    );
});
